import * as React from 'react'
import { AppShell } from './components/shell/AppShell'
import { LeaveGuardDialog } from './components/shell/LeaveGuardDialog'
import type { RecentItem } from './components/shell/Sidebar'
import { TooltipProvider } from './components/ui/Tooltip'
import { listPersistedSessions } from './lib/session/sessionPersistence'
import { remoteListSessions } from './lib/session/sessionsRemote'
import { deleteSession, renameSession } from './lib/session/sessionActions'
import { confirmLeave, isLeaveGuardActive } from './lib/session/leaveGuard'
import { Account } from './pages/Account'
import { Contacts } from './pages/Contacts'
import { Home } from './pages/Home'
import { Session } from './pages/Session'
import { Settings } from './pages/Settings'
import { SpikePage } from './spike/SpikePage'

// Excalidraw is a large dependency — code-split the board route so it never enters
// the #/session entry chunk (PRD 010 AC4). Loaded only when #/board is navigated to.
const Board = React.lazy(() =>
  import('./pages/Board').then((m) => ({ default: m.Board }))
)

// Minimal hash router — keeps the app a single static bundle with no router dep.
function useHashRoute(): string {
  const [hash, setHash] = React.useState(() => window.location.hash || '#/')
  React.useEffect(() => {
    const onHash = () => setHash(window.location.hash || '#/')
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  return hash
}

// '#/session' -> undefined ; '#/session/<id>' -> '<id>'
function parseSessionId(route: string): string | undefined {
  const id = route.match(/^#\/session\/(.+)$/)?.[1]
  return id ? decodeURIComponent(id) : undefined
}

// Recent sessions for the sidebar. The server is the system of record (PRD 011):
// list from it, then union in any IndexedDB-only sessions (offline / pre-sync) so
// nothing disappears, preferring a local title when the server's is still blank.
// Falls back to the IndexedDB cache entirely when the backend is unreachable.
// Refreshed on every route change (so a just-created session shows up) and
// whenever `reloadKey` changes (after a rename/delete).
function useRecentSessions(route: string, reloadKey: number): RecentItem[] {
  const [recent, setRecent] = React.useState<RecentItem[]>([])
  React.useEffect(() => {
    let cancelled = false
    Promise.all([remoteListSessions(), listPersistedSessions()]).then(([remote, local]) => {
      if (cancelled) return
      const items: RecentItem[] = []
      const seen = new Set<string>()
      if (remote) {
        for (const s of remote) {
          const localTitle = local.find((l) => l.id === s.sessionId)?.title
          items.push({ id: s.sessionId, title: s.title || localTitle || 'Untitled session' })
          seen.add(s.sessionId)
        }
      }
      for (const s of local) {
        if (seen.has(s.id)) continue
        items.push({ id: s.id, title: s.title })
      }
      setRecent(items)
    })
    return () => {
      cancelled = true
    }
  }, [route, reloadKey])
  return recent
}

export function App() {
  const route = useHashRoute()
  const [reloadKey, setReloadKey] = React.useState(0)
  const recent = useRecentSessions(route, reloadKey)
  const reloadRecent = () => setReloadKey((k) => k + 1)

  // While a brainstorm round is live, gate every navigation away from it (the
  // round ends on leave — the agent stops listening). Cover the two paths the
  // hash router can't see: anchor clicks (nav links, the wordmark) and a full
  // unload (reload / tab close). Programmatic jumps (session select, New session)
  // route through confirmLeave() directly at their call sites. (Declared before
  // any early return so the hook order is stable.)
  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!isLeaveGuardActive() || e.defaultPrevented) return
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const target = e.target as HTMLElement | null
      const anchor = target?.closest?.('a[href^="#/"]') as HTMLAnchorElement | null
      const href = anchor?.getAttribute('href')
      if (!href || href === window.location.hash) return
      e.preventDefault()
      void confirmLeave().then((ok) => {
        if (ok) window.location.hash = href
      })
    }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isLeaveGuardActive()) return
      e.preventDefault()
      e.returnValue = ''
    }
    document.addEventListener('click', onClick, true)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('click', onClick, true)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [])

  // The Phase-0 spike harness is a full-bleed dev tool, not part of the app
  // shell — render it standalone (docs/prd/001 §4).
  if (route.startsWith('#/spike')) {
    return (
      <TooltipProvider>
        <SpikePage />
      </TooltipProvider>
    )
  }

  const activeId = parseSessionId(route)
  const onSelect = (id: string) => {
    // Re-selecting the session you're already in isn't leaving — don't gate it.
    if (id === activeId) return
    void confirmLeave().then((ok) => {
      if (ok) window.location.hash = `#/session/${encodeURIComponent(id)}`
    })
  }
  const onRename = (id: string, title: string) => {
    void renameSession(id, title).then(reloadRecent)
  }
  const onDelete = (id: string) => {
    void deleteSession(id).then(() => {
      // If the open session was deleted, leave the view (its controller unmounts
      // and disposes; the deleted-guard stops a late flush resurrecting it).
      if (id === activeId) window.location.hash = '#/'
      reloadRecent()
    })
  }

  let page: React.ReactNode
  if (route.startsWith('#/settings')) {
    page = <Settings />
  } else if (route.startsWith('#/account')) {
    page = <Account />
  } else if (route.startsWith('#/contacts')) {
    page = <Contacts />
  } else if (route.startsWith('#/board')) {
    page = (
      <React.Suspense
        fallback={
          <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
            Loading whiteboard…
          </div>
        }
      >
        <Board />
      </React.Suspense>
    )
  } else if (route.startsWith('#/session')) {
    page = <Session sessionId={activeId} />
  } else {
    page = <Home />
  }

  return (
    <TooltipProvider>
      <AppShell
        route={route}
        recent={recent}
        activeId={activeId}
        onSelect={onSelect}
        onRename={onRename}
        onDelete={onDelete}
      >
        {page}
      </AppShell>
      <LeaveGuardDialog />
    </TooltipProvider>
  )
}
